import type { ReactElement } from "react"
import { useContext } from "react"
import type { Props as FloatingButtonMolProps } from "@onestore/hel/dist/components/molecules/FloatingButtonMol"
import FloatingButtonMol from "@onestore/hel/dist/components/molecules/FloatingButtonMol"
import FormilyFormButton from "@gatsby-plugin-generic-page/components/FormilyFormButton"
import type { CtaElement } from "@gatsby-plugin-generic-page/fragments/ctaElement"
import type { FloatingCtaElementSectionFlatData } from "@gatsby-plugin-generic-page/fragments/floatingCtaElementSection"
import { IconPosition } from "@gatsby-plugin-generic-page/fragments/floatingCtaElementSection"
import type { Formily as FormilyType } from "@gatsby-plugin-generic-page/fragments/formily"
import { getCtaButtonFlatData } from "@gatsby-plugin-generic-page/helpers/getCtaElementFlatData"
import { replaceLinkValues } from "@gatsby-plugin-generic-page/helpers/linkParser"
import { SquidexFormType } from "@gatsby-plugin-generic-page/types/formType"
import { Picture } from "@ionos-web-design-system/react"
import { typeMatches } from "@onestore-graphql"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import SearchUrlParametersContext from "~/context/SearchUrlParametersContext"
import { sendGaEvent } from "~/lib/analytics"
import assetsSquidexToImageObject from "~/lib/assetsSquidexToImageObject"
import isEmpty from "~/lib/isEmpty"
import FloatingButtonWithAnchor from "../FloatingButtonWithAnchor"

type Props = FloatingCtaElementSectionFlatData & {
  isVisible: boolean
}

export type FloatingButtonMolCommonProps = Pick<
  FloatingButtonMolProps,
  "title" | "text" | "isVisible" | "icon"
>

function FloatingCtaElement({
  ctaButton,
  isVisible,
  icon,
  iconPosition,
}: Props): ReactElement<FloatingCtaElementSectionFlatData> | null {
  const { currentPriceType } = usePriceTypeContext()
  const currentSearchUrlParameters = useContext(SearchUrlParametersContext)

  if (isEmpty(ctaButton)) {
    return null
  }

  const { text, title, gaEvent, anchorId, form, href, openInNewTab, pages } =
    getCtaButtonFlatData(ctaButton[0] as CtaElement, currentPriceType)

  const commonProps: FloatingButtonMolCommonProps = {
    icon: !isEmpty(icon) ? (
      <Picture srcData={assetsSquidexToImageObject(icon, "")} />
    ) : null,
    title: title,
    text: text,
    isVisible: isVisible,
  }

  if (anchorId) {
    return (
      <FloatingButtonWithAnchor
        {...commonProps}
        anchorId={anchorId}
        iconPosition={iconPosition}
      />
    )
  }

  if (
    !isEmpty(form) &&
    typeMatches(form[0].__typename, SquidexFormType.FORMILY_FORM)
  ) {
    return (
      <FormilyFormButton
        button={(onClick) => (
          <FloatingButtonMol
            {...commonProps}
            iconPosition={iconPosition ? IconPosition[iconPosition] : undefined}
            onClick={onClick}
          />
        )}
        formilyForm={form[0] as FormilyType}
      />
    )
  }

  return (
    <FloatingButtonMol
      {...commonProps}
      iconPosition={iconPosition ? IconPosition[iconPosition] : undefined}
      href={replaceLinkValues(href, currentSearchUrlParameters)}
      to={!isEmpty(pages) ? pages[0].flatData.url : undefined}
      onClick={() => sendGaEvent(gaEvent)}
      openInNewTab={openInNewTab}
    />
  )
}

export default FloatingCtaElement
